import { InteractionRequiredAuthError, type AccountInfo, type AuthenticationResult, type IPublicClientApplication } from '@azure/msal-browser';
import { gatewayApiAccessTokenRequest } from '@/auth/config';
import * as WebViewHelper from '@/library/WebViewHelper';
import { isIOSWebView } from '@/utils';

const API_ACCESS_TOKEN_STORAGE_KEY = 'tour.apiAccessToken';
const EXPIRY_SKEW_MS = 60 * 1000; // 1 minute buffer to avoid using near-expired tokens
const IOS_GATEWAY_TOKEN_FALLBACK_EXPIRY_MS = 60 * 60 * 1000;
const IOS_GATEWAY_TOKEN_REQUEST_TIMEOUT_MS = 10 * 1000;

export const IMAGE_PROVIDER_CONNECTION_ERROR_MESSAGE = 'Failed to connect to image provider';

type CachedApiAccessToken = {
  accessToken: string;
  expiresOn: number;
};

let pendingIOSGatewayTokenRequest: Promise<string> | null = null;

const setCachedApiAccessToken = (token: CachedApiAccessToken) => {
  if (!navigator.onLine) {
    return;
  }

  window.sessionStorage.setItem(API_ACCESS_TOKEN_STORAGE_KEY, JSON.stringify(token));
};

const getCachedApiAccessToken = (): CachedApiAccessToken | null => {
  const raw = window.sessionStorage.getItem(API_ACCESS_TOKEN_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as CachedApiAccessToken;
    if (!parsed.accessToken || !parsed.expiresOn) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const getCachedGwApiAccessToken = () => {
  const cachedToken = getCachedApiAccessToken();
  if (!cachedToken || !isTokenValid(cachedToken)) {
    return '';
  }

  return cachedToken.accessToken;
};

const isTokenValid = (token: CachedApiAccessToken) => {
  return Date.now() + EXPIRY_SKEW_MS < token.expiresOn;
};

const mapAuthResultToCachedToken = (result: AuthenticationResult): CachedApiAccessToken => {
  return {
    accessToken: result.accessToken,
    expiresOn: result.expiresOn?.getTime() || Date.now(),
  };
};

export const cacheGwApiAccessTokenResult = (result: AuthenticationResult) => {
  const token = mapAuthResultToCachedToken(result);
  setCachedApiAccessToken(token);
  return token;
};

export const cacheGwApiAccessToken = (accessToken: string, expiresOn = Date.now() + IOS_GATEWAY_TOKEN_FALLBACK_EXPIRY_MS) => {
  const token: CachedApiAccessToken = {
    accessToken,
    expiresOn,
  };

  setCachedApiAccessToken(token);
  return token;
};

const getRequestAccount = (instance: IPublicClientApplication, account?: AccountInfo) => {
  if (account) {
    return account;
  }

  return instance.getActiveAccount() || instance.getAllAccounts()[0] || null;
};

export const clearGwApiAccessTokenCache = () => {
  window.sessionStorage.removeItem(API_ACCESS_TOKEN_STORAGE_KEY);
};

export const gwApiAccessTokenOfflineCleanup = () => {
  if (!navigator.onLine) {
    clearGwApiAccessTokenCache();
  }

  const handleOffline = () => {
    clearGwApiAccessTokenCache();
  };

  window.addEventListener('offline', handleOffline);

  return () => {
    window.removeEventListener('offline', handleOffline);
  };
};

const clearPendingIOSGatewayTokenRequest = () => {
  pendingIOSGatewayTokenRequest = null;
  delete window.iOSGatewayTokenResponse;
};

const requestIOSGwApiAccessToken = async (forceRefresh = false): Promise<string> => {
  if (pendingIOSGatewayTokenRequest) {
    return pendingIOSGatewayTokenRequest;
  }

  let requestTimeout!: ReturnType<typeof setTimeout>;
  const tokenRequest = new Promise<string>((resolve, reject) => {
    requestTimeout = setTimeout(() => {
      const error = new Error(IMAGE_PROVIDER_CONNECTION_ERROR_MESSAGE);
      console.error(error.message);
      clearPendingIOSGatewayTokenRequest();
      reject(error);
    }, IOS_GATEWAY_TOKEN_REQUEST_TIMEOUT_MS);

    window.iOSGatewayTokenResponse = (accessToken, expiresOn) => {
      clearTimeout(requestTimeout);
      clearPendingIOSGatewayTokenRequest();

      if (!accessToken) {
        const error = new Error('iOS API Gateway token request failed.');
        console.error(error.message);
        reject(error);
        return;
      }

      const token = cacheGwApiAccessToken(accessToken, expiresOn);
      resolve(token.accessToken);
    };
  });
  pendingIOSGatewayTokenRequest = tokenRequest;

  try {
    WebViewHelper.iOSGatewayTokenRequest({ forceRefresh });
  } catch (error) {
    console.error('Failed to request iOS API Gateway token.', error);
    clearTimeout(requestTimeout);
    clearPendingIOSGatewayTokenRequest();
    throw error;
  }

  return tokenRequest;
};

export const acquireGwApiAccessToken = async (instance?: IPublicClientApplication, account?: AccountInfo, forceRefresh = false): Promise<string> => {
  if (isIOSWebView) {
    // Adding this check defensively
    return requestIOSGwApiAccessToken(forceRefresh);
  }

  if (!instance) {
    throw new Error('No MSAL instance available to acquire API access token.');
  }

  const requestAccount = getRequestAccount(instance, account);
  if (!requestAccount) {
    throw new Error('No authenticated account available to acquire API access token.');
  }

  try {
    const response = await instance.acquireTokenSilent({
      ...gatewayApiAccessTokenRequest,
      account: requestAccount,
      forceRefresh,
    });

    const token = cacheGwApiAccessTokenResult(response);
    return token.accessToken;
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) {
      throw error;
    }

    const response = await instance.acquireTokenPopup({
      ...gatewayApiAccessTokenRequest,
      account: requestAccount,
    });

    const token = cacheGwApiAccessTokenResult(response);
    return token.accessToken;
  }
};

export const getValidGwApiAccessToken = async (instance?: IPublicClientApplication, account?: AccountInfo): Promise<string> => {
  const cachedToken = getCachedApiAccessToken();
  if (cachedToken && isTokenValid(cachedToken)) {
    return cachedToken.accessToken;
  }

  if (isIOSWebView) {
    return requestIOSGwApiAccessToken();
  }

  return acquireGwApiAccessToken(instance, account);
};

export const refreshGwApiAccessToken = async (instance?: IPublicClientApplication, account?: AccountInfo): Promise<string> => {
  clearGwApiAccessTokenCache();

  if (isIOSWebView) {
    return requestIOSGwApiAccessToken(true);
  }

  return acquireGwApiAccessToken(instance, account, true);
};
